
import { rangeLabel, clockLabel, sMin, eMin } from '../../utils/shiftTime';
import React from 'react';
import { Plus, Clock, Users, User } from 'lucide-react';
import { Shift, Staff, DailyEvent, ShiftType, Position, getJSTDateString, createJSTDate , Duty } from '../../api';
import { DutyMarks } from '../StaffDuties';

interface ShiftCalendarViewProps {
    currentDate: Date;
    confirmedShifts: Shift[];
    events: DailyEvent[];
    shiftTypes: ShiftType[];
    allStaff: Staff[];
    positions: Position[];
    openAddEventModal: (dateStr: string, event?: DailyEvent) => void;
    openShiftEditModal: (shift: Shift) => void;
    onShowDay?: (dateStr: string) => void;
    markDuties?: Duty[];
}

const ShiftCalendarView: React.FC<ShiftCalendarViewProps> = ({
    currentDate,
    confirmedShifts,
    events,
    shiftTypes,
    allStaff,
    positions,
    openAddEventModal,
    openShiftEditModal,
    onShowDay,
    markDuties = []
}) => {
    const todayStr = getJSTDateString(new Date());
    
    const getCalendarDays = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        
        const days = [];
        const startPad = firstDay.getDay();
        
        for (let i = 0; i < startPad; i++) days.push(null);
        for (let i = 1; i <= lastDay.getDate(); i++) {
            days.push(createJSTDate(year, month + 1, i));
        }
        
        return days;
    };

    const getPositionColor = (posId?: string) => {
        const pos = positions.find(p => p.id === posId);
        return pos ? pos.color : 'bg-gray-100 text-gray-700';
    };

    const days = getCalendarDays();

    return (
        <div className="w-full h-full overflow-y-auto bg-[#F8FAFC]">
            {/* デスクトップ用グリッドビュー (1024px以上) */}
            <div className="hidden lg:grid grid-cols-7 gap-4 p-4 auto-rows-min bg-slate-50">
                {['日', '月', '火', '水', '木', '金', '土'].map((day, i) => (
                    <div key={day} className={`text-center font-black text-sm mb-2 ${i===0 ? 'text-red-500' : i===6 ? 'text-blue-500' : 'text-slate-500'}`}>
                        {day}
                    </div>
                ))}
                
                {days.map((date, idx) => {
                    if (!date) return <div key={idx} className="bg-transparent" />;
                    const dateStr = getJSTDateString(date);
                    const dayShifts = confirmedShifts.filter(s => s.start.startsWith(dateStr));
                    const isToday = dateStr === todayStr;
                    const isPast = dateStr < todayStr;
                    const dayEvents = events.filter(e => {
                        const start = e.date;
                        const end = e.endDate || e.date;
                        return dateStr >= start && dateStr <= end;
                    });

                    // 土日の背景色
                    const isSun = date.getDay() === 0;
                    const isSat = date.getDay() === 6;
                    const weekendBg = isSun ? 'bg-sunday/30' : isSat ? 'bg-saturday/30' : 'bg-white';

                    return (
                        <div 
                          key={dateStr} 
                          className={`relative rounded-lg border ${isToday ? 'border-blue-400 shadow-md ring-1 ring-blue-100' : 'border-gray-200'} p-2 flex flex-col min-h-[132px] transition-all hover:shadow-md group/cal ${isPast ? 'opacity-70' : ''} ${weekendBg}`}
                        >
                            <div className={`relative z-10 text-xs font-bold mb-1 flex justify-between items-center ${isSun ? 'text-red-500' : isSat ? 'text-blue-500' : 'text-slate-400'}`}>
                                <div className="flex items-center gap-1.5 ml-auto">
                                    {isToday && (
                                        <span className="bg-blue-600 text-white px-1.5 py-0.5 rounded text-[10px] whitespace-nowrap shadow-sm">今日</span>
                                    )}
                                    <span className={`text-sm ${isPast ? 'opacity-50 font-normal' : 'font-black'}`}>
                                        {date.getDate()}
                                    </span>
                                </div>
                            </div>
                            
                            {!isPast && (
                                <button 
                                    onClick={(e) => { 
                                        e.preventDefault(); e.stopPropagation(); openAddEventModal(dateStr); 
                                    }}
                                    className="absolute top-2 left-2 w-7 h-7 flex items-center justify-center bg-white hover:bg-blue-600 text-slate-400 hover:text-white rounded-lg transition-all z-[40] shadow-sm border border-slate-200 cursor-pointer"
                                >
                                    <Plus size={14} strokeWidth={3} />
                                </button>
                            )}
                            
                            <div className="flex-1 space-y-1">
                                {dayEvents.map(ev => (
                                    <div key={ev.id} onClick={() => openAddEventModal(dateStr, ev)} className="text-[10px] bg-amber-50 text-amber-800 px-1.5 py-1 rounded truncate font-black border border-amber-100 shadow-sm cursor-pointer">{ev.title}</div>
                                ))}
                                {/* 人数不足は1行にまとめ、スタッフの表示を押し出さない */}
                                {dayShifts.some(sh => sh.type === 'vacancy') && (
                                    <div onClick={() => onShowDay?.(dateStr)} className="text-[10px] px-2 py-1 rounded font-bold border border-dashed border-red-300 bg-red-50 text-red-600 leading-tight cursor-pointer truncate">
                                        人数不足 {Array.from(new Set(dayShifts.filter(sh => sh.type === 'vacancy').map(sh => positions.find(p => p.id === sh.positionId)?.name || ''))).join('・')}
                                    </div>
                                )}
                                {dayShifts.filter(sh => sh.type !== 'vacancy').slice(0, 2).map(shift => {
                                    const staff = allStaff.find(s => s.id === shift.staffId);
                                    const style = getPositionColor(shift.positionId);
                                    if (shift.type === 'vacancy') {
                                        const posName = positions.find(p => p.id === shift.positionId)?.name || '';
                                        return (
                                            <div key={shift.id} onClick={() => onShowDay?.(dateStr)} className="text-[10px] px-2 py-1 rounded font-bold border border-dashed border-red-300 bg-red-50 text-red-600 leading-tight cursor-pointer">
                                                <div className="truncate">不足{shift.headcount || 1}名　{posName}</div>
                                                <div className="opacity-80 text-[9px] font-medium tabular-nums whitespace-nowrap">{rangeLabel(shift)}</div>
                                            </div>
                                        );
                                    }
                                    return (
                                        <div key={shift.id} onClick={() => !isPast && openShiftEditModal(shift)} className={`text-[10px] px-2 py-1 rounded font-bold border border-black/5 shadow-sm leading-tight ${style} ${isPast ? 'opacity-70' : 'cursor-pointer hover:brightness-95'}`}>
                                            <div className="flex items-center gap-1 min-w-0"><span className="truncate">{staff ? `${staff.lastName} ${staff.firstName}` : '未定'}</span><DutyMarks staff={staff} duties={markDuties} size="xs" /></div>
                                            <div className="opacity-70 text-[9px] font-medium tabular-nums whitespace-nowrap">{rangeLabel(shift)}</div>
                                        </div>
                                    );
                                })}
                                {dayShifts.filter(sh => sh.type !== 'vacancy').length > 2 && (
                                    <button type="button" onClick={() => onShowDay?.(dateStr)} className="w-full text-left text-[10px] font-bold text-blue-600 hover:underline px-1 whitespace-nowrap">+{dayShifts.filter(sh => sh.type !== 'vacancy').length - 2}名（この日を見る）</button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* モバイル用リストビュー (1024px未満) */}
            <div className="lg:hidden flex flex-col divide-y divide-slate-100 bg-white">
                {days.filter(d => d !== null).map((date) => {
                    if (!date) return null;
                    const dateStr = getJSTDateString(date);
                    const dayShifts = confirmedShifts.filter(s => s.start.startsWith(dateStr));
                    const isToday = dateStr === todayStr;
                    const isPast = dateStr < todayStr;
                    const dayEvents = events.filter(e => {
                        const start = e.date;
                        const end = e.endDate || e.date;
                        return dateStr >= start && dateStr <= end;
                    });

                    // 土日の色判定用ロジック
                    const getWeekendStyles = (d: Date) => {
                        if (isToday) return 'bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-100';
                        if (d.getDay() === 0) return 'bg-red-50 border-red-100 text-red-500';
                        if (d.getDay() === 6) return 'bg-blue-50 border-blue-100 text-blue-500';
                        return 'bg-slate-50 border-slate-100 text-slate-400';
                    };

                    const dateStyles = getWeekendStyles(date);

                    // 予定がない日も日付だけは表示
                    if (dayShifts.length === 0 && dayEvents.length === 0) {
                        return (
                            <div 
                                key={dateStr} 
                                onClick={() => !isPast && openAddEventModal(dateStr)}
                                className={`p-4 flex items-center gap-4 transition-colors ${isPast ? 'bg-slate-50/50' : 'bg-white active:bg-slate-50 cursor-pointer'}`}
                            >
                                <div className={`relative w-12 h-12 rounded-xl flex flex-col items-center justify-center shrink-0 border ${dateStyles}`}>
                                    <span className="text-[10px] font-black uppercase leading-none mb-1">{['日','月','火','水','木','金','土'][date.getDay()]}</span>
                                    <span className="text-lg font-black leading-none">{date.getDate()}</span>
                                    {!isPast && (
                                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 text-white rounded-full flex items-center justify-center border-2 border-white shadow-sm">
                                            <Plus size={8} strokeWidth={4} />
                                        </div>
                                    )}
                                </div>
                                <div className="text-xs font-bold text-slate-300">
                                    {isPast ? '履歴なし' : 'タップして行事・予定を追加'}
                                </div>
                            </div>
                        );
                    }

                    return (
                        <div key={dateStr} className={`p-4 flex gap-4 ${isPast ? 'bg-slate-50/50' : 'bg-white'}`}>
                            {/* 左側：日付カード */}
                            <div className="flex flex-col items-center gap-2 shrink-0">
                                <button 
                                    disabled={isPast}
                                    onClick={() => openAddEventModal(dateStr)}
                                    className={`relative w-12 h-12 rounded-xl flex flex-col items-center justify-center border transition-transform active:scale-95 ${dateStyles} ${isPast ? 'opacity-50' : 'cursor-pointer hover:border-blue-300'}`}
                                >
                                    <span className="text-[10px] font-black uppercase leading-none mb-1">{['日','月','火','水','木','金','土'][date.getDay()]}</span>
                                    <span className="text-lg font-black leading-none">{date.getDate()}</span>
                                    {!isPast && (
                                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 text-white rounded-full flex items-center justify-center border-2 border-white shadow-sm">
                                            <Plus size={8} strokeWidth={4} />
                                        </div>
                                    )}
                                </button>
                                {dayShifts.length > 0 && (
                                    <div className="text-[8px] font-black text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-full uppercase tracking-tighter">
                                        {dayShifts.length}名
                                    </div>
                                )}
                            </div>

                            {/* 右側：コンテンツ（行事とシフト詳細） */}
                            <div className="flex-1 space-y-3">
                                {dayEvents.map(ev => (
                                    <div key={ev.id} onClick={() => openAddEventModal(dateStr, ev)} className="bg-amber-50 border border-amber-100 rounded-lg p-3 shadow-sm flex items-center justify-between active:scale-95 transition-transform cursor-pointer">
                                        <span className="text-sm font-black text-amber-900">{ev.title}</span>
                                        <span className="text-[9px] font-bold text-amber-600 bg-white/60 px-2 py-0.5 rounded-full uppercase tracking-widest border border-amber-200/50">行事</span>
                                    </div>
                                ))}

                                <div className="grid grid-cols-1 gap-2">
                                    {dayShifts.map(shift => {
                                        const staff = allStaff.find(s => s.id === shift.staffId);
                                        const pos = positions.find(p => p.id === shift.positionId);
                                        const posColor = getPositionColor(shift.positionId);
                                        const startTime = clockLabel(sMin(shift));
                                        const endTime = clockLabel(eMin(shift));

                                        return (
                                            <div key={shift.id} onClick={() => !isPast && openShiftEditModal(shift)} className={`bg-white border border-slate-100 rounded-xl p-3 shadow-sm flex items-center justify-between ${isPast ? 'opacity-60' : 'active:scale-[0.98] cursor-pointer'} transition-all`}>
                                                <div className="flex items-center gap-3 overflow-hidden">
                                                    {/* ポジションカラーバー */}
                                                    <div className={`w-1.5 h-10 rounded-full shrink-0 ${posColor.split(' ')[0]}`} />
                                                    
                                                    <div className="truncate">
                                                        <div className="flex items-center gap-1.5">
                                                            <div className="text-sm font-black text-slate-800 truncate">
                                                                {staff ? `${staff.lastName} ${staff.firstName}` : 'スタッフ未定'}
                                                                <DutyMarks staff={staff} duties={markDuties} />
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2 mt-1">
                                                            <div className="flex items-center text-[10px] text-blue-600 font-black bg-blue-50 px-1.5 py-0.5 rounded">
                                                                <Clock size={10} className="mr-1" />
                                                                {startTime}〜{endTime}
                                                            </div>
                                                            {shift.label && (
                                                                <span className="text-[9px] font-bold text-slate-400 border border-slate-200 px-1.5 rounded">{shift.label}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* ポジション名ラベル */}
                                                <div className="ml-2 text-right">
                                                    <div className="text-[10px] font-black text-slate-400 bg-slate-50 px-2 py-1 rounded-lg whitespace-nowrap border border-slate-100">
                                                        {pos?.name || 'その他'}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ShiftCalendarView;
